import { useState } from 'react'
import posthog from 'posthog-js'
import { Veriff, type VeriffPersonData } from '@veriff/js-sdk'
import { createVeriffFrame, MESSAGES } from '@veriff/incontext-sdk'
import { useSettings } from '~/@mid_settings/settings'
import { useLogging } from '~/@mid_logging/logging/useLogging'
import { useUser } from '~/@biz_account/useUser/useUser'
import { getVeriffCallbackUrl } from './veriffCallback'

const veriffRootId = 'veriff-root'

function useVeriff(isWebView = false) {
  const { user } = useUser()
  const { veriff: veriffSettings } = useSettings()
  const { mutate: createLog } = useLogging()
  const [isVeriffPending, setIsVeriffPending] = useState(false)

  return {
    veriffRootId: veriffRootId,
    isVeriffPending,
    setIsVeriffPending,
    handleVeriffVerification: (props: {
      person: VeriffPersonData
      vendorData: string
      onVeriffFinished: (msg: MESSAGES) => void
    }) => {
      createLog({
        level: 'info',
        message: `VERIFF tool opened by user ${user?.profile?.email}`,
        subject: 'veriff',
        event: 'doVeriff',
      })

      if (!veriffSettings) {
        createLog({
          level: 'error',
          message: 'VERIFF settings missing on client side!',
          subject: 'veriff',
          event: 'doVeriff',
        })
        return
      }

      const veriffClient = Veriff({
        host: veriffSettings.host,
        apiKey: veriffSettings.apiKey,
        parentId: veriffRootId,
        onSession: function (
          err: unknown,
          response: { verification: { url: string } } | null
        ) {
          if (err) {
            posthog.captureException(err)
            createLog({
              level: 'error',
              message: `VERIFF failed to start session: ${JSON.stringify(err)}`,
              subject: 'veriff',
              event: 'doVeriff',
            })
            return
          }

          if (!response) {
            createLog({
              level: 'error',
              message: 'VERIFF no response from onSession callback',
              subject: 'veriff',
              event: 'doVeriff',
            })
            return
          }

          const verificationUrl = response.verification.url

          if (isWebView) {
            const redirectUrl = new URL(verificationUrl)
            redirectUrl.searchParams.set('lang', 'sk')
            window.location.assign(redirectUrl.toString())
            return
          }

          createVeriffFrame({
            url: verificationUrl,
            lang: 'sk',
            onEvent: props.onVeriffFinished,
          })
        },
      })

      veriffClient.setParams({
        person: props.person,
        vendorData: props.vendorData,
        ...(isWebView ? { callback: getVeriffCallbackUrl() } : {}),
      })

      veriffClient.mount({
        submitBtnText: 'Overiť',
        loadingText: 'Prosím čakajte...',
        formLabel: {
          vendorData: 'Email',
        },
      })

      setTimeout(() => {
        const veriffSubmitButton = window?.document?.querySelector(
          'form[name=veriff-form] .veriff-submit'
        ) as HTMLElement
        if (veriffSubmitButton) {
          veriffSubmitButton.click()
        }
      }, 1)
    },
  }
}

export { useVeriff }
